import { Chart, registerables } from 'chart.js';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { supabaseClient } from '../../services/supabase';
import { downloadChartAsPNG, downloadDataAsCSV, formatFilename } from '../../utils/downloadUtils';
import { ChartLayout, OverviewContent, RelevanceContent, EquityContent, MethodologyContent } from './ChartLayout';
import { KeyMetrics, KeyMetric } from './KeyMetrics';
import './ChartLayout.css';
import { standardLegend, standardTitle } from '../../utils/chartFormatting';

Chart.register(...registerables);

export const SingleFamilyDetached = ({ location, geofips, preferredState = null, yearStart = 2010, yearEnd = 2023 }) => {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [locationData, setLocationData] = useState([]);
  const [californiaData, setCaliforniaData] = useState([]);
  const [activeView, setActiveView] = useState('overview');

  const years = useMemo(
    () => Array.from({ length: (yearEnd - yearStart + 1) }, (_, i) => yearStart + i),
    [yearStart, yearEnd]
  );

  useEffect(() => {
    const fetchData = async () => {
      if (!location || location === '') {
        setLoading(false);
        return;
      }
      
      if (!geofips) {
        setError('A geofips is required to render this chart.');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Check if this is California state (geofips 6)
        const isCaliforniaState = geofips === 6 || (location && location.toLowerCase() === 'california');
        
        let locationDataResult = null;
        
        if (isCaliforniaState) {
          const { data: stateData, error: stateErr } = await supabaseClient
            .from('single_family_detached')
            .select('*')
            .eq('geography_type', 'state')
            .eq('geoid', 6)
            .gte('year', yearStart)
            .lte('year', yearEnd)
            .order('year', { ascending: true });
          
          if (stateErr) {
            console.error('Error fetching California state data:', stateErr);
            throw stateErr;
          }
          
          locationDataResult = stateData || [];
        } else {
          // Try place (city) data first, then county
          let query = supabaseClient
            .from('single_family_detached')
            .select('*')
            .eq('geography_type', 'place')
            .gte('year', yearStart)
            .lte('year', yearEnd)
            .order('year', { ascending: true });
          
          if (location) {
            query = query.ilike('location_name', `%${location}%`);
          } else if (geofips) {
            query = query.eq('geoid', geofips);
          }
          
          const { data: placeData, error: placeErr } = await query;
          
          if (placeErr) {
            console.error('Error fetching place data:', placeErr);
          }
          
          if (placeData && placeData.length > 0) {
            locationDataResult = placeData;
          } else {
            
            // Try county data
            let countyQuery = supabaseClient
              .from('single_family_detached')
              .select('*')
              .eq('geography_type', 'county')
              .gte('year', yearStart)
              .lte('year', yearEnd)
              .order('year', { ascending: true });
            
            if (geofips) {
              countyQuery = countyQuery.eq('geoid', geofips);
            } else if (location) {
              countyQuery = countyQuery.ilike('location_name', `%${location}%`);
            }
            
            const { data: countyData, error: countyErr } = await countyQuery;
            
            if (countyErr) {
              console.error('Error fetching county data:', countyErr);
            }
            
            locationDataResult = countyData || [];
          }
        }

        // Always fetch California state data for comparison (unless this IS California)
        let californiaDataResult = [];
        if (!isCaliforniaState) {
          const { data: caData, error: caErr } = await supabaseClient
            .from('single_family_detached')
            .select('*')
            .eq('geography_type', 'state')
            .eq('geoid', 6)
            .gte('year', yearStart)
            .lte('year', yearEnd)
            .order('year', { ascending: true });
          
          if (caErr) {
            console.error('Error fetching California comparison data:', caErr);
          } else {
            californiaDataResult = caData || [];
          }
        }

        setLocationData(locationDataResult || []);
        setCaliforniaData(californiaDataResult || []);
        setLoading(false);
      } catch (e) {
        console.error('Error loading single_family_detached:', e);
        setError(e.message || 'Failed to load single family detached data');
        setLoading(false);
      }
    };

    fetchData();
  }, [geofips, yearStart, yearEnd, location]);

  // Calculate key metrics
  const keyMetrics = useMemo(() => {
    if (loading || locationData.length === 0) return null;
    
    const yearsData = locationData.map(r => r.year).filter(y => !isNaN(y));
    if (yearsData.length === 0) return null;
    
    const latestYear = Math.max(...yearsData);
    const firstYear = Math.min(...yearsData);
    
    const latestRow = locationData.find(r => r.year === latestYear);
    const firstRow = locationData.find(r => r.year === firstYear);
    
    if (!latestRow || latestRow.percentage_single_family_detached === null || latestRow.percentage_single_family_detached === undefined) {
      return null;
    }
    
    const latestPercent = Number(latestRow.percentage_single_family_detached);
    
    let percentChange = null;
    let firstPercent = null;
    if (firstRow && firstRow.percentage_single_family_detached !== null && firstRow.percentage_single_family_detached !== undefined) {
      firstPercent = Number(firstRow.percentage_single_family_detached);
      percentChange = latestPercent - firstPercent;
    }
    
    if (isNaN(latestPercent)) {
      return null;
    }

    let caLatestPercent = null;
    if (californiaData.length > 0) {
      const caRow = californiaData.find(r => r.year === latestYear);
      if (caRow && caRow.percentage_single_family_detached != null) {
        caLatestPercent = Number(caRow.percentage_single_family_detached);
      }
    }
    
    return {
      latestPercent: latestPercent,
      firstPercent: firstPercent,
      percentChange: percentChange,
      latestYear: latestYear,
      firstYear: firstYear,
      caLatestPercent: caLatestPercent
    };
  }, [loading, locationData, californiaData]);

  // Build the chart after data loads
  useEffect(() => {
    if (loading || locationData.length === 0) return;

    const ctx = chartRef.current?.getContext('2d');
    if (!ctx) return;

    if (chartInstance.current) {
      chartInstance.current.destroy();
    }

    // Prepare data for location
    const locationPercentData = years.map(year => {
      const row = locationData.find(r => r.year === year);
      const value = row && row.percentage_single_family_detached !== null ? row.percentage_single_family_detached : null;
      return value;
    });

    // Prepare data for California
    const californiaPercentData = years.map(year => {
      const row = californiaData.find(r => r.year === year);
      const value = row && row.percentage_single_family_detached !== null ? row.percentage_single_family_detached : null;
      return value;
    });

    const chartTitle = `Single-Family Detached Homes (%) - ${location || 'Selected Location'}`;

    const datasets = [
      {
        label: location || 'Selected Location',
        data: locationPercentData,
        borderColor: '#3b82f6', // Blue
        backgroundColor: '#3b82f633',
        borderWidth: 2,
        spanGaps: true,
        tension: 0.25,
        pointRadius: 3
      }
    ];

    // Only add California line if we have data and this is not California
    if (californiaData.length > 0) {
      datasets.push({
        label: 'California',
        data: californiaPercentData,
        borderColor: '#ef4444', // Red
        backgroundColor: '#ef444433',
        borderWidth: 2,
        spanGaps: true,
        tension: 0.25,
        pointRadius: 3
      });
    }

    chartInstance.current = new Chart(ctx, {
      type: 'line',
      data: {
        labels: years,
        datasets: datasets
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        interaction: { mode: 'nearest', intersect: false },
        scales: {
          y: {
            title: { display: true, text: 'Percent (%)' },
            suggestedMin: 0,
            suggestedMax: 100,
            ticks: {
              callback: (v) => `${v}%`
            }
          },
          x: {
            title: { display: true, text: 'Year' }
          }
        },
        plugins: {
          title: standardTitle(chartTitle),
          legend: standardLegend,
          tooltip: {
            callbacks: {
              label: (ctx) => {
                const val = ctx.parsed.y;
                return `${ctx.dataset.label}: ${val != null ? val.toFixed(1) : 'N/A'}%`;
              }
            }
          }
        }
      }
    });
  }, [loading, years, locationData, californiaData, location]);

  const handleDownloadPNG = () => {
    const filename = formatFilename(location, 'Single_Family_Detached');
    downloadChartAsPNG(chartRef, filename);
  };

  const handleDownloadCSV = () => {
    const rows = locationData.map(row => ({
      year: row.year,
      geoid: row.geoid,
      location_name: row.location_name,
      geography_type: row.geography_type,
      percentage_single_family_detached: row.percentage_single_family_detached,
      margin_of_error: row.margin_of_error
    }));
    downloadDataAsCSV(rows, formatFilename(location, 'Single_Family_Detached_Data'));
  };

  if (loading) {
    return (
      <div style={{ height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div>Loading single family detached data for {location || 'selected location'}...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ height: 400, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
        <div style={{ color: '#ef4444', marginBottom: '10px' }}>{error}</div>
        <div style={{ color: '#6b7280', fontSize: '14px' }}>Location: {location || 'Not set'}</div>
        <div style={{ color: '#6b7280', fontSize: '14px' }}>Geofips: {geofips || 'Not set'}</div>
      </div>
    );
  }
  
  if (!location) {
    return (
      <div style={{ padding: '20px', textAlign: 'center', color: '#6b7280' }}>
        Please select a location to view Single Family Detached data.
      </div>
    );
  }
  
  if (!loading && locationData.length === 0) {
    return (
      <div style={{ padding: '20px', textAlign: 'center', color: '#6b7280' }}>
        No single family detached data available for {location}.
      </div>
    );
  }

  const chartContent = (
    <div style={{ height: 400, width: '100%', position: 'relative' }}>
      <canvas ref={chartRef} style={{ height: '100%', width: '100%' }} />
    </div>
  );

  // Key metrics display: Location %, CA %, % Change over period
  const keyMetricsDisplay = keyMetrics && keyMetrics.latestPercent !== undefined ? (
    <KeyMetrics>
      <KeyMetric
        value={`${keyMetrics.latestPercent.toFixed(1)}%`}
        label={`% Single-Family Detached Homes in ${location}`}
        color="#3b82f6"
      />
      <KeyMetric
        value={keyMetrics.caLatestPercent != null ? `${keyMetrics.caLatestPercent.toFixed(1)}%` : '—'}
        label="% Single-Family Detached Homes in CA"
        color="#10b981"
      />
      <KeyMetric
        value={
          keyMetrics.percentChange !== null
            ? `${keyMetrics.percentChange >= 0 ? '+' : ''}${keyMetrics.percentChange.toFixed(1)} percentage points`
            : '—'
        }
        label={`Change in detached share (${keyMetrics.firstYear}–${keyMetrics.latestYear}), percentage points`}
        color={keyMetrics.percentChange !== null && keyMetrics.percentChange < 0 ? '#10b981' : '#ef4444'}
      />
    </KeyMetrics>
  ) : null;

  const generateKeyInsights = () => {
    if (!keyMetrics) return null;
    const { latestYear, firstYear, latestPercent, percentChange, caLatestPercent } = keyMetrics;
    const pp =
      percentChange != null
        ? `${percentChange >= 0 ? '+' : ''}${percentChange.toFixed(1)} percentage points`
        : '—';
    return (
      <p>
        In {latestYear}, {latestPercent.toFixed(1)}% of housing units were single-family detached
        {caLatestPercent != null ? `, compared with ${caLatestPercent.toFixed(1)}% statewide` : ''}. The local share changed by approximately {pp} from {firstYear} to {latestYear}. This indicates housing form; it is not, by itself, a measure of sprawl.
      </p>
    );
  };

  return (
    <ChartLayout
      title="Single-Family Detached Homes"
      key={`single-family-detached-${location}`}
      description="The share of housing units reported as single-family detached, compared with California."
      chartType="line"
      keyMetrics={keyMetricsDisplay}
      chartContent={chartContent}
      activeView={activeView}
      onViewChange={setActiveView}
      showRelevanceEquity={true}
      layout="side-by-side"
      onDownloadPNG={handleDownloadPNG}
      onDownloadCSV={handleDownloadCSV}
      pngFilename={formatFilename(location, 'Single_Family_Detached')}
      csvFilename={formatFilename(location, 'Single_Family_Detached_Data')}
      enableComments={true}
      location={location}
      appendLocationToTitle={true}
      overviewContent={
        <OverviewContent
          title="Overview"
          description={`This chart shows the percentage of housing units that are single-family detached homes in ${location} and in California from ${keyMetrics ? keyMetrics.firstYear : '—'} to ${keyMetrics ? keyMetrics.latestYear : '—'}.`}
        >
          <div>{generateKeyInsights()}</div>
        </OverviewContent>
      }
      relevanceContent={
        <RelevanceContent
          title="Relevance"
          description="This indicator measures the share of total housing units that are single-family detached homes. Housing type distribution reflects development patterns, residential density, land consumption, and long-term growth form."
        >
          <div className="methodology-details-simple">
            <h5>Land Use and Density</h5>
            <p>A higher share of single-family detached homes generally corresponds to lower-density development and greater land consumption per household. A lower share typically indicates a greater presence of multifamily or attached housing and more compact development patterns.</p>
          </div>
          <div className="methodology-details-simple">
            <h5>Transportation and Climate</h5>
            <p>Lower-density housing patterns are often associated with higher per-capita vehicle miles traveled (VMT) due to greater separation between housing, jobs, and services. More compact housing mixes may support transit use, walking, and biking, depending on urban design and connectivity.</p>
          </div>
        </RelevanceContent>
      }
      equityContent={
        <EquityContent
          title="Equity"
          description="Housing type distribution has implications for affordability, tenure patterns, and access to housing opportunities. However, this indicator does not alone measure housing cost, income levels, or displacement risk."
        >
          <div className="methodology-details-simple">
            <h5>Affordability and Entry Barriers</h5>
            <p>Single-family detached homes are often more expensive and more likely to be owner-occupied. A high share of detached housing may limit entry-level or rental housing options.</p>
          </div>
          <div className="methodology-details-simple">
            <h5>Tenure and Access</h5>
            <p>Communities with a more diversified housing stock may offer a wider range of rental and ownership opportunities across income levels.</p>
          </div>
          <div className="methodology-details-simple">
            <h5>Spatial Patterns</h5>
            <p>If different housing types are concentrated in specific areas, disparities in access to schools, transit, or amenities may emerge.</p>
          </div>
        </EquityContent>
      }
      methodologyContent={
        <MethodologyContent
          title="Data Sources and Methodology"
          description="Data are from the American Community Survey 5-Year sample, with the last year of the 5-year sample representing the current year. The US Census conducts a rolling sample of U.S. households every year."
          dataSources={[
            'American Community Survey (ACS) 5-Year Estimates',
            'Housing units by structure type',
            'US Census rolling household sample'
          ]}
          methodology={[
            'Percentage single-family detached is derived from total number of homes per type of structure',
            'Other structure types are mostly multi-family, but also include mobile homes, condominiums, and other attached structures',
            'Data filtered by geography type (place, county, or state) and location',
            'Percentile rankings calculated against all locations in the dataset'
          ]}
        />
      }
    />
  );
};

export default SingleFamilyDetached;

